// client/src/coms/settings/settingsRegistry.js
// Personal Settings: the categories (left navigation) and a searchable index of every setting. Each entry's
// `id` is also the DOM anchor of its row (`vx-setting-<id>`), so a search result can open the category and
// scroll to the control. `scope` says where the value lives: the account (every device), this device/browser,
// or one server.

export const SCOPES = {
  account: { label: 'Account', title: 'Saved to your account: follows you to every device you sign in on.' },
  device: { label: 'This device', title: 'Saved in this browser or app only.' },
  server: { label: 'This server', title: 'Applies only in the selected server.' },
};

export const CATEGORIES = [
  { id: 'account', label: 'My Account', icon: '👤', description: 'Your avatar and signed-in devices.' },
  { id: 'voice', label: 'Voice & Video', icon: '🎙️', description: 'Microphone sensitivity, camera, screen sharing and push-to-talk.' },
  { id: 'audio', label: 'Audio Devices', icon: '🎧', description: 'Which microphone and speakers Voxemera uses, and a level test.' },
  { id: 'animations', label: 'Speaking Animations', icon: '〰️', description: 'The animation others see next to your name while you talk.' },
  { id: 'layout', label: 'Layout & Interface', icon: '🧩', description: 'Desktop layout presets, panel arrangement, tabs and split view.' },
  { id: 'mobile', label: 'Mobile Layout', icon: '📱', description: 'Layouts, navigation, gestures and spacing on phones; separate from desktop.' },
  { id: 'themes', label: 'Themes & Accessibility', icon: '🎨', description: 'Colours, text size, scaling, motion and focus outlines.' },
  { id: 'notifications', label: 'Notifications', icon: '🔔', description: 'Do Not Disturb, desktop notifications and unread badges.' },
  { id: 'privacy', label: 'Privacy & Security', icon: '🛡️', description: 'Online status, end-to-end encryption, moderation notices and appeals.' },
  { id: 'connections', label: 'Connections & Integrations', icon: '🔗', description: 'Steam, cloud storage and music services.' },
  { id: 'stickers', label: 'Stickers & Sounds', icon: '🎟️', description: 'Your sticker packs and soundboard sounds.' },
  { id: 'shortcuts', label: 'Keyboard Shortcuts', icon: '⌨️', description: 'Mute hotkeys and the shortcuts for tabs and panels.' },
  { id: 'server', label: 'This Server', icon: '🏠', description: 'Personal preferences that apply only in the current server.' },
  { id: 'advanced', label: 'Advanced', icon: '🧰', description: 'Where settings are stored, diagnostics and resets.' },
];

export const CATEGORY_IDS = new Set(CATEGORIES.map((c) => c.id));

// label / description are what users see; keywords add synonyms for search.
export const SETTINGS_INDEX = [
  // My Account
  { id: 'avatar', cat: 'account', label: 'Avatar', keywords: 'profile picture photo image', scope: 'account' },
  { id: 'devices', cat: 'account', label: 'Signed-in devices', keywords: 'sessions sign out log out security browsers', scope: 'account' },
  { id: 'accounts', cat: 'account', label: 'Switch accounts', keywords: 'multiple accounts add account switch', scope: 'device' },

  // Voice & Video
  { id: 'noise-gate', cat: 'voice', label: 'Microphone sensitivity (noise gate)', keywords: 'threshold background noise gate sensitivity quiet', scope: 'device' },
  { id: 'ptt', cat: 'voice', label: 'Push-to-talk', keywords: 'ptt talk key hotkey walkie', scope: 'device' },
  { id: 'camera', cat: 'voice', label: 'Camera', keywords: 'webcam video device', scope: 'device' },
  { id: 'screen', cat: 'voice', label: 'Screen sharing', keywords: 'share screen stream quality resolution fps', scope: 'device' },

  // Audio devices
  { id: 'input-device', cat: 'audio', label: 'Microphone', keywords: 'input mic device headset', scope: 'device' },
  { id: 'output-device', cat: 'audio', label: 'Speakers / headphones', keywords: 'output speaker headphones device sink', scope: 'device' },
  { id: 'mic-test', cat: 'audio', label: 'Microphone test', keywords: 'level meter test volume db', scope: 'device' },
  { id: 'permissions', cat: 'audio', label: 'Browser permissions', keywords: 'allow microphone camera permission blocked', scope: 'device' },

  // Speaking animations
  { id: 'speaking-animation', cat: 'animations', label: 'Speaking animation', keywords: 'voice animation indicator talking equaliser bars dots ring glow', scope: 'account' },
  { id: 'speaking-test', cat: 'animations', label: 'Test speaking', keywords: 'preview try demo microphone', scope: 'device' },
  { id: 'speaking-motion', cat: 'animations', label: 'Speaking animations with reduced motion', keywords: 'still badge animate reduce motion', scope: 'account' },

  // Layout & interface
  { id: 'layout-preset', cat: 'layout', label: 'Layout preset', keywords: 'classic compact focus social creator custom', scope: 'account' },
  { id: 'arrangement', cat: 'layout', label: 'Panel arrangement', keywords: 'left right hidden channels members move panel side', scope: 'account' },
  { id: 'customise-on-screen', cat: 'layout', label: 'Customise on screen', keywords: 'edit layout drag live', scope: 'account' },
  { id: 'panel-widths', cat: 'layout', label: 'Channel and member list width', keywords: 'width size sidebar resize', scope: 'account' },
  { id: 'spacing', cat: 'layout', label: 'Spacing and chat density', keywords: 'compact cozy comfortable density', scope: 'account' },
  { id: 'panels', cat: 'layout', label: 'Tab bar, pinned banner and Activities button', keywords: 'tab bar header banner activities placement beside chat', scope: 'account' },
  { id: 'server-nav', cat: 'layout', label: 'Server navigation', keywords: 'server list rail dropdown sidebar', scope: 'device' },
  { id: 'tabs', cat: 'layout', label: 'Conversation tabs and split view', keywords: 'tabs reopen restore split', scope: 'account' },

  // Mobile layout
  { id: 'mobile-preset', cat: 'mobile', label: 'Mobile layout preset', keywords: 'phone mobile classic compact top bottom dropdown gesture custom preview apply', scope: 'account' },
  { id: 'mobile-nav', cat: 'mobile', label: 'Navigation on phones', keywords: 'navigation bottom bar top tabs dropdown menu icons emoji labels hide scrolling reorder destinations header phone', scope: 'account' },
  { id: 'mobile-saved', cat: 'mobile', label: 'Saved mobile layouts', keywords: 'save layout custom phone restore', scope: 'account' },
  { id: 'mobile-recovery', cat: 'mobile', label: 'Restore the mobile layout', keywords: 'reset recover lost navigation broken phone defaults', scope: 'account' },
  { id: 'mobile-voice', cat: 'mobile', label: 'Voice controls on phones', keywords: 'voice dock mute deafen leave call phone', scope: 'account' },
  { id: 'mobile-density', cat: 'mobile', label: 'Spacing on phones', keywords: 'compact cozy density phone mobile', scope: 'account' },
  { id: 'mobile-text', cat: 'mobile', label: 'Message text size on phones', keywords: 'font size text phone mobile', scope: 'account' },
  { id: 'mobile-swipe', cat: 'mobile', label: 'Swipe gestures', keywords: 'gesture swipe drawer phone on off', scope: 'account' },
  { id: 'mobile-gestures', cat: 'mobile', label: 'What each swipe does', keywords: 'gesture swipe left right up down previous next conversation menu switcher phone', scope: 'account' },

  // Themes & accessibility
  { id: 'theme', cat: 'themes', label: 'Theme', keywords: 'dark mode light mode oled black high contrast system colours colors appearance', scope: 'account' },
  { id: 'accent', cat: 'themes', label: 'Accent colour', keywords: 'accent color highlight', scope: 'account' },
  { id: 'custom-themes', cat: 'themes', label: 'Your themes', keywords: 'custom theme create import export editor', scope: 'account' },
  { id: 'text-size', cat: 'themes', label: 'Message text size', keywords: 'font size text larger smaller readability', scope: 'account' },
  { id: 'ui-scale', cat: 'themes', label: 'Interface scale', keywords: 'zoom scale bigger smaller', scope: 'account' },
  { id: 'reduce-motion', cat: 'themes', label: 'Reduce motion', keywords: 'animation motion vestibular', scope: 'account' },
  { id: 'focus-ring', cat: 'themes', label: 'Focus outlines', keywords: 'keyboard focus outline ring visibility', scope: 'account' },
  { id: 'server-themes', cat: 'themes', label: 'Server theme suggestions', keywords: 'server theme suggest offer', scope: 'account' },

  // Notifications
  { id: 'dnd', cat: 'notifications', label: 'Do Not Disturb', keywords: 'mute notifications quiet dnd snooze', scope: 'account' },
  { id: 'desktop-notifications', cat: 'notifications', label: 'Desktop notifications', keywords: 'push notifications browser alerts', scope: 'device' },
  { id: 'unread-badges', cat: 'notifications', label: 'Unread badges per server', keywords: 'badges mentions unread counts', scope: 'server' },

  // Privacy & safety
  { id: 'status', cat: 'privacy', label: 'Status: online, AFK, Do Not Disturb, appear offline', keywords: 'appear offline invisible presence status online hidden idle afk away moon dnd do not disturb duration timer', scope: 'account' },
  { id: 'status-message', cat: 'privacy', label: 'Custom status message', keywords: 'status message custom text presence', scope: 'account' },
  { id: 'e2ee-dm', cat: 'privacy', label: 'End-to-end encryption for direct messages', keywords: 'e2ee encryption encrypted encrypt lock secure private security dm direct messages', scope: 'account' },
  { id: 'moderation', cat: 'privacy', label: 'Moderation & appeals', keywords: 'warnings timeouts bans kicks appeal', scope: 'account' },

  // Connections
  { id: 'steam', cat: 'connections', label: 'Steam account', keywords: 'steam games profile', scope: 'account' },
  { id: 'storage', cat: 'connections', label: 'Cloud storage (Google Drive)', keywords: 'google drive files storage dropbox onedrive', scope: 'account' },
  { id: 'spotify', cat: 'connections', label: 'Spotify', keywords: 'music spotify watch together', scope: 'device' },

  // Stickers & sounds
  { id: 'sticker-packs', cat: 'stickers', label: 'Sticker packs', keywords: 'stickers packs import export suggestions', scope: 'account' },
  { id: 'soundboard', cat: 'stickers', label: 'Soundboard sounds', keywords: 'soundboard sounds clips packs', scope: 'account' },

  // Shortcuts
  { id: 'mute-hotkeys', cat: 'shortcuts', label: 'Mute hotkeys', keywords: 'mute deafen speaker hotkey keyboard', scope: 'device' },
  { id: 'tab-shortcuts', cat: 'shortcuts', label: 'Tab and panel shortcuts', keywords: 'alt shift keyboard tabs split', scope: 'device' },

  // This server
  { id: 'server-prefs', cat: 'server', label: 'Preferences for this server', keywords: 'per server override landing channel layout theme', scope: 'server' },

  // Advanced
  { id: 'storage-map', cat: 'advanced', label: 'Where your settings are stored', keywords: 'local storage keys sync account device', scope: 'device' },
  { id: 'diagnostics', cat: 'advanced', label: 'Device diagnostics', keywords: 'devices counts support debug', scope: 'device' },
  { id: 'reset-device', cat: 'advanced', label: 'Reset this device’s settings', keywords: 'reset clear local defaults', scope: 'device' },
];

// Combining accents (U+0300 to U+036F), built from code points so the source stays ASCII.
const COMBINING_MARKS = new RegExp(`[${String.fromCharCode(0x300)}-${String.fromCharCode(0x36f)}]`, 'g');

function norm(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(COMBINING_MARKS, '');
}

// Every word of the query must appear in the label, keywords or category name (in any order).
export function searchSettings(query, index = SETTINGS_INDEX) {
  const words = norm(query).split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const catLabel = Object.fromEntries(CATEGORIES.map((c) => [c.id, c.label]));
  const scored = [];
  for (const e of index) {
    const label = norm(e.label);
    const hay = `${label} ${norm(e.keywords)} ${norm(catLabel[e.cat])}`;
    if (!words.every((w) => hay.includes(w))) continue;
    const score = words.reduce((n, w) => n + (label.startsWith(w) ? 3 : label.includes(w) ? 2 : 1), 0);
    scored.push({ ...e, score, catLabel: catLabel[e.cat] });
  }
  return scored.sort((a, b) => b.score - a.score || a.label.localeCompare(b.label));
}

// Older callers open Settings by tab name (e.g. window.__voxPendingSettingsTab = 'Stickers').
const LEGACY_TABS = {
  audio: 'audio',
  video: 'voice',
  screen: 'voice',
  account: 'account',
  appearance: 'layout',
  stickers: 'stickers',
  soundboard: 'stickers',
  moderation: 'privacy',
};

const APPEARANCE_SECTIONS = { theme: 'themes', layout: 'layout', tabs: 'layout', server: 'server' };

export function resolveCategory(tab, appearanceSection = '') {
  const t = String(tab || '').trim();
  if (CATEGORY_IDS.has(t)) return t;
  const lower = t.toLowerCase();
  if (lower === 'appearance' && APPEARANCE_SECTIONS[appearanceSection]) return APPEARANCE_SECTIONS[appearanceSection];
  return LEGACY_TABS[lower] || '';
}

export function anchorForLegacyTab(tab, appearanceSection = '') {
  const lower = String(tab || '').toLowerCase();
  if (lower === 'soundboard') return 'soundboard';
  if (lower === 'screen') return 'screen';
  if (lower === 'video') return 'camera';
  if (lower === 'appearance' && appearanceSection === 'tabs') return 'tabs';
  return '';
}

// Layout settings belong to one kind of screen: phones show Mobile Layout only, larger screens Layout & Interface
// only. A request for the other one (an old link, a remembered category) opens this screen's own layout settings.
export function hiddenCategoryFor(isMobile) {
  return isMobile ? 'layout' : 'mobile';
}

export function categoriesFor(isMobile) {
  const hidden = hiddenCategoryFor(isMobile);
  return CATEGORIES.filter((c) => c.id !== hidden);
}

export function categoryForDevice(cat, isMobile) {
  if (cat === hiddenCategoryFor(isMobile)) return isMobile ? 'mobile' : 'layout';
  return cat;
}
